import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { CalendarDays, Check, Circle, Plus, RotateCcw, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { TESTIDS } from "@/constants/testIds";
import { api } from "@/lib/api";
import { useI18n } from "@/lib/i18n";

const EMPTY_FORM = { title: "", due_date: "", priority: "normal", deal_id: "", assignee_id: "" };

function detail(error, fallback) {
  const value = error?.response?.data?.detail;
  return typeof value === "string" ? value : fallback;
}

function dueState(dueDate) {
  if (!dueDate) return "none";
  const today = new Date().toISOString().slice(0, 10);
  if (dueDate < today) return "overdue";
  if (dueDate === today) return "today";
  return "future";
}

export default function TasksPage() {
  const { t } = useI18n();
  const [tasks, setTasks] = useState([]);
  const [deals, setDeals] = useState([]);
  const [members, setMembers] = useState([]);
  const [filter, setFilter] = useState("open");
  const [form, setForm] = useState(EMPTY_FORM);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [taskItems, dealItems, memberItems] = await Promise.all([api.listTasks(), api.listDeals(), api.listOrganizationMembers()]);
      setTasks(taskItems);
      setDeals(dealItems);
      setMembers(memberItems);
    } catch (error) {
      toast.error(detail(error, t("tasks_update_failed")));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const dealById = useMemo(
    () => Object.fromEntries(deals.map((deal) => [deal.id, deal])),
    [deals],
  );
  const memberById = useMemo(
    () => Object.fromEntries(members.map((member) => [member.user_id, member])),
    [members],
  );
  const visibleTasks = useMemo(
    () => tasks.filter((task) => filter === "all" || task.status === filter),
    [tasks, filter],
  );

  const createTask = async (event) => {
    event.preventDefault();
    setSaving(true);
    try {
      const created = await api.createTask({
        title: form.title,
        due_date: form.due_date || null,
        priority: form.priority,
        deal_id: form.deal_id || null,
        assignee_id: form.assignee_id || null,
      });
      setTasks((items) => [created, ...items]);
      setForm(EMPTY_FORM);
      setFilter("open");
      toast.success(t("tasks_created"));
    } catch (error) {
      toast.error(detail(error, t("tasks_create_failed")));
    } finally {
      setSaving(false);
    }
  };

  const toggleTask = async (task) => {
    const nextStatus = task.status === "completed" ? "open" : "completed";
    try {
      const updated = await api.updateTask(task.id, { status: nextStatus });
      setTasks((items) => items.map((item) => item.id === task.id ? updated : item));
    } catch (error) {
      toast.error(detail(error, t("tasks_update_failed")));
    }
  };

  const removeTask = async (task) => {
    if (!window.confirm(t("tasks_delete_confirm"))) return;
    try {
      await api.deleteTask(task.id);
      setTasks((items) => items.filter((item) => item.id !== task.id));
    } catch (error) {
      toast.error(detail(error, t("tasks_delete_failed")));
    }
  };

  const emptyText = {
    open: t("tasks_empty_open"),
    completed: t("tasks_empty_completed"),
    all: t("tasks_empty_all"),
  }[filter];

  return (
    <div className="px-4 py-6 sm:p-6 md:p-10 xl:p-12 max-w-6xl">
      <div className="mb-10">
        <div className="kicker mb-3">— {t("tasks_title")}</div>
        <h1 className="font-serif text-4xl md:text-5xl mb-3">{t("tasks_title")}</h1>
        <p className="text-[var(--td-text-2)] max-w-2xl leading-relaxed">{t("tasks_intro")}</p>
      </div>

      <section className="card-surface p-4 sm:p-6 mb-8">
        <h2 className="font-serif text-2xl mb-5">{t("tasks_add_title")}</h2>
        <form data-testid={TESTIDS.taskCreateForm} onSubmit={createTask}
          className="grid lg:grid-cols-3 gap-3 items-end">
          <div>
            <label className="td-label">{t("tasks_task")}</label>
            <input required maxLength={160} className="td-input" data-testid={TESTIDS.taskTitle}
              placeholder={t("tasks_task_placeholder")} value={form.title}
              onChange={(event) => setForm((value) => ({ ...value, title: event.target.value }))} />
          </div>
          <div>
            <label className="td-label">{t("tasks_due")}</label>
            <input type="date" className="td-input" data-testid={TESTIDS.taskDueDate}
              value={form.due_date}
              onChange={(event) => setForm((value) => ({ ...value, due_date: event.target.value }))} />
          </div>
          <div>
            <label className="td-label">{t("tasks_priority")}</label>
            <select className="td-input" data-testid={TESTIDS.taskPriority} value={form.priority}
              onChange={(event) => setForm((value) => ({ ...value, priority: event.target.value }))}>
              <option value="low">{t("tasks_priority_low")}</option>
              <option value="normal">{t("tasks_priority_normal")}</option>
              <option value="high">{t("tasks_priority_high")}</option>
            </select>
          </div>
          <div>
            <label className="td-label">{t("tasks_deal")}</label>
            <select className="td-input" data-testid={TESTIDS.taskDeal} value={form.deal_id}
              onChange={(event) => setForm((value) => ({ ...value, deal_id: event.target.value }))}>
              <option value="">{t("tasks_no_deal")}</option>
              {deals.map((deal) => <option key={deal.id} value={deal.id}>{deal.reference ? `${deal.reference} · ` : ""}{deal.title}</option>)}
            </select>
          </div>
          <div>
            <label className="td-label">{t("tasks_assignee")}</label>
            <select className="td-input" data-testid={TESTIDS.taskAssignee} value={form.assignee_id}
              onChange={(event) => setForm((value) => ({ ...value, assignee_id: event.target.value }))}>
              <option value="">{t("tasks_unassigned")}</option>
              {members.map((member) => <option key={member.user_id} value={member.user_id}>{member.full_name}</option>)}
            </select>
          </div>
          <button type="submit" disabled={saving} data-testid={TESTIDS.taskCreate}
            className="btn-gold inline-flex items-center justify-center gap-2 disabled:opacity-50 whitespace-nowrap">
            <Plus size={14} /> {t("tasks_add")}
          </button>
        </form>
      </section>

      <div className="flex flex-wrap items-center gap-2 mb-4" data-testid={TESTIDS.taskFilter}>
        {["open", "completed", "all"].map((value) => (
          <button key={value} type="button" onClick={() => setFilter(value)}
            className={`font-mono text-[10px] uppercase tracking-widest px-3 py-1.5 border transition-colors ${
              filter === value
                ? "border-[var(--td-gold)] text-[var(--td-gold)]"
                : "border-[var(--td-border)] text-[var(--td-text-3)] hover:text-[var(--td-text)]"
            }`}>
            {t(`tasks_filter_${value}`)}
          </button>
        ))}
      </div>

      <section className="card-surface">
        {loading ? (
          <div className="p-8 text-[var(--td-text-3)] font-mono text-xs">…</div>
        ) : visibleTasks.length === 0 ? (
          <div className="p-10 text-center text-[var(--td-text-2)] italic">{emptyText}</div>
        ) : (
          <ul>
            {visibleTasks.map((task) => {
              const relatedDeal = task.deal_id ? dealById[task.deal_id] : null;
              const due = task.status === "open" ? dueState(task.due_date) : "none";
              return (
                <li key={task.id} data-testid={TESTIDS.taskRow}
                  className="flex items-start gap-3 sm:gap-4 p-4 sm:p-5 border-b border-[var(--td-border)] last:border-b-0">
                  <button type="button" onClick={() => toggleTask(task)} data-testid={TESTIDS.taskToggle}
                    aria-label={task.status === "completed" ? t("tasks_status_open") : t("tasks_status_completed")}
                    className="mt-0.5 text-[var(--td-gold)] hover:text-[var(--td-gold-hover)]">
                    {task.status === "completed" ? <RotateCcw size={18} /> : <Circle size={18} />}
                  </button>
                  <div className="min-w-0 flex-1">
                    <div className={`font-serif text-lg ${task.status === "completed" ? "line-through text-[var(--td-text-3)]" : ""}`}>
                      {task.title}
                    </div>
                    <div className="flex flex-wrap gap-x-3 gap-y-1 mt-1 text-xs text-[var(--td-text-3)]">
                      <span className={`font-mono uppercase tracking-widest ${task.priority === "high" ? "text-amber-400" : ""}`}>
                        {t(`tasks_priority_${task.priority}`)}
                      </span>
                      {task.due_date && (
                        <span className={`inline-flex items-center gap-1 ${due === "overdue" ? "text-red-400" : due === "today" ? "text-amber-400" : ""}`}>
                          <CalendarDays size={12} /> {task.due_date}
                          {due === "overdue" && ` · ${t("tasks_overdue")}`}
                          {due === "today" && ` · ${t("tasks_due_today")}`}
                        </span>
                      )}
                      {relatedDeal && <Link to={`/app/deals/${relatedDeal.id}`} className="text-[var(--td-gold)] hover:underline">{relatedDeal.reference || relatedDeal.title}</Link>}
                      {task.assignee_id && memberById[task.assignee_id] && <span>{t("tasks_assignee")}: {memberById[task.assignee_id].full_name}</span>}
                    </div>
                  </div>
                  {task.status === "completed" && <Check size={16} className="mt-1 text-emerald-400" />}
                  <button type="button" onClick={() => removeTask(task)} data-testid={TESTIDS.taskDelete}
                    aria-label={t("tasks_delete_confirm")}
                    className="mt-0.5 text-[var(--td-text-3)] hover:text-red-400">
                    <Trash2 size={15} />
                  </button>
                </li>
              );
            })}
          </ul>
        )}
      </section>
    </div>
  );
}
